import React from "react";
import { Instagram, Facebook, Twitter, Phone, MessageSquare, CheckCircle2, Lock } from "lucide-react";
import { Settings, Category } from "../types";
import { DEFAULT_RENTLOW_NAP } from "../utils/seoOptimizationEngine";
import { APP_VERSION_INFO } from "../version";

interface FooterProps {
  settings: Settings | null;
  categories: Category[];
  currentTab: string;
  onSelectCategory: (catId: string) => void;
  onSelectTab: (tabId: string) => void;
  onOpenPolicy: (policyId: string) => void;
  onTrackSupportClick?: (type: "whatsapp" | "call") => void;
  onGoHome?: () => void;
}

export default function Footer({
  settings,
  categories,
  currentTab,
  onSelectCategory,
  onSelectTab,
  onOpenPolicy,
  onTrackSupportClick,
  onGoHome
}: FooterProps) {
  const currentYear = new Date().getFullYear();

  // Load support info from settings or fallback to default
  const whatsappNumber = settings?.whatsappPhone || "918770781490";
  const supportPhone = settings?.supportPhone || "+919300691533";
  const instagramUrl = settings?.instagramUrl || "https://instagram.com/rentlow.co.in";
  const facebookUrl = settings?.facebookUrl || "https://facebook.com/rentlow.co.in";
  const twitterUrl = settings?.twitterUrl || "https://twitter.com/rentlow_india";
  const googleBusinessUrl = settings?.googleBusinessUrl || DEFAULT_RENTLOW_NAP.googleMapsUrl;

  // Deduplicate and curate categories for pristine presentation without duplicates or case mismatches
  const uniqueCategoryMap = new Map<string, Category>();
  (categories && categories.length > 0 
    ? categories 
    : [
        { id: "air-conditioners", name: "Air Conditioners (AC)" },
        { id: "washing-machines", name: "Washing Machines" },
        { id: "refrigerators", name: "Refrigerators" },
        { id: "coolers", name: "Desert Coolers" },
        { id: "water-purifiers", name: "Water Purifiers" }
      ]
  ).forEach(cat => {
    const rawName = cat.name.trim();
    const normalizedKey = rawName.toLowerCase();
    if (!uniqueCategoryMap.has(normalizedKey)) {
      uniqueCategoryMap.set(normalizedKey, {
        ...cat,
        name: rawName.charAt(0).toUpperCase() + rawName.slice(1)
      });
    }
  });
  const displayCategories = Array.from(uniqueCategoryMap.values());

  const handleCategoryClick = (e: React.MouseEvent, catId: string) => {
    e.preventDefault();
    onSelectCategory(catId);
    onSelectTab("home");
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const handleTabClick = (e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    onSelectTab(tabId);
    if (tabId === "home") {
      onSelectCategory("all");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-gray-950 text-gray-300 mt-16 border-t border-gray-900">
      
      {/* Upper Footer section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12">
          
          {/* Logo & About Section */}
          <div className="space-y-4 lg:col-span-1">
            <button
              onClick={(e) => {
                e.preventDefault();
                if (onGoHome) {
                  onGoHome();
                } else {
                  handleTabClick(e, "home");
                }
              }}
              className="flex flex-col items-start focus:outline-none cursor-pointer group text-left transition-transform active:scale-95 py-1 pr-2 touch-manipulation select-none"
              aria-label="RentLow Home - Back to Storefront"
              title="RentLow Home"
            >
              <div className="flex items-baseline font-sans text-2xl font-black tracking-tight">
                <span className="text-white group-hover:text-sky-400 transition-colors">Rent</span>
                <span className="text-[#0077B6] font-black">Low</span>
              </div>
              <span className="text-[10px] font-extrabold tracking-widest text-sky-400 font-mono -mt-1 uppercase">
                रेंट से लो
              </span>
            </button>
            <p className="text-sm text-slate-400 leading-relaxed">
              Your premium, hassle-free rental appliance partner in Bhopal and Sehore. Rent high-end fridges, washing machines, coolers, smart TVs, and purifiers at guaranteed lowest monthly subscription rates with zero advance online payments.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <a 
                href={instagramUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="p-2 bg-slate-900 border border-slate-800 hover:border-[#0077B6] hover:text-[#0077B6] rounded-lg text-slate-300 transition-colors cursor-pointer"
                aria-label="Visit RentLow official Instagram page"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href={facebookUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="p-2 bg-slate-900 border border-slate-800 hover:border-[#0077B6] hover:text-[#0077B6] rounded-lg text-slate-300 transition-colors cursor-pointer"
                aria-label="Visit RentLow official Facebook page"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href={twitterUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="p-2 bg-slate-900 border border-slate-800 hover:border-[#0077B6] hover:text-[#0077B6] rounded-lg text-slate-300 transition-colors cursor-pointer"
                aria-label="Visit RentLow official Twitter/X page"
              >
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Navigation Links Column */}
          <div className="space-y-4">
            <h3 className="text-white text-sm font-bold tracking-wider uppercase font-sans">
              Quick Navigation
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <a 
                  href="/"
                  onClick={(e) => handleTabClick(e, "home")}
                  className={`font-medium transition-colors cursor-pointer text-left block ${
                    currentTab === "home" ? "text-teal-400 font-bold" : "text-gray-400 hover:text-teal-400"
                  }`}
                >
                  Home Landing Page
                </a>
              </li>
              <li>
                <a 
                  href="/?tab=upcoming-launchpad"
                  onClick={(e) => handleTabClick(e, "upcoming-launchpad")}
                  className={`font-medium transition-colors cursor-pointer text-left block ${
                    currentTab === "upcoming-launchpad" ? "text-teal-400 font-bold" : "text-gray-400 hover:text-teal-400"
                  }`}
                >
                  Upcoming Models
                </a>
              </li>
              <li>
                <a 
                  href="/?tab=bookings"
                  onClick={(e) => handleTabClick(e, "bookings")}
                  className={`font-medium transition-colors cursor-pointer text-left block ${
                    currentTab === "bookings" ? "text-teal-400 font-bold" : "text-gray-400 hover:text-teal-400"
                  }`}
                >
                  My Subscriptions
                </a>
              </li>
              <li>
                <a 
                  href={googleBusinessUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-300 text-amber-400 font-extrabold transition-colors cursor-pointer text-left flex items-center gap-1.5 font-sans text-xs"
                >
                  <span className="bg-amber-400 text-gray-950 font-black rounded-full h-4 w-4 flex items-center justify-center text-[10px]">G</span>
                  <span>Google Business Store (4.9 ⭐)</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Categories Column */}
          <div className="space-y-4">
            <h3 className="text-white text-sm font-bold tracking-wider uppercase font-sans">
              Rental Categories
            </h3>
            <ul className="space-y-2.5 text-sm">
              {displayCategories.map((cat) => (
                <li key={cat.id}>
                  <a 
                    href={`/?category=${cat.id}`}
                    onClick={(e) => handleCategoryClick(e, cat.id)}
                    className="hover:text-teal-400 text-gray-400 font-medium transition-colors cursor-pointer text-left block"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick CMS Links */}
          <div className="space-y-4">
            <h3 className="text-white text-sm font-bold tracking-wider uppercase font-sans">
              Our Policies
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <a 
                  href="/?policy=terms"
                  onClick={(e) => { e.preventDefault(); onOpenPolicy("terms"); }} 
                  className="hover:text-teal-400 text-gray-400 font-medium transition-colors cursor-pointer text-left block"
                >
                  Terms & Conditions
                </a>
              </li>
              <li>
                <a 
                  href="/?policy=privacy"
                  onClick={(e) => { e.preventDefault(); onOpenPolicy("privacy"); }} 
                  className="hover:text-teal-400 text-gray-400 font-medium transition-colors cursor-pointer text-left block"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a 
                  href="/?policy=payment"
                  onClick={(e) => { e.preventDefault(); onOpenPolicy("payment"); }} 
                  className="hover:text-teal-400 text-gray-400 font-medium transition-colors cursor-pointer text-left block"
                >
                  Payment Terms
                </a>
              </li>
              <li>
                <a 
                  href="/?policy=refund"
                  onClick={(e) => { e.preventDefault(); onOpenPolicy("refund"); }} 
                  className="hover:text-teal-400 text-gray-400 font-medium transition-colors cursor-pointer text-left block"
                >
                  Refund & Escrow
                </a>
              </li>
              <li>
                <a 
                  href="/return-policy"
                  onClick={(e) => { e.preventDefault(); onOpenPolicy("return-policy"); }} 
                  className="hover:text-teal-400 text-gray-400 font-medium transition-colors cursor-pointer text-left block"
                >
                  Return & Replacement Policy
                </a>
              </li>
            </ul>
          </div>

          {/* Help & Support Desk */}
          <div className="space-y-4">
            <h3 className="text-white text-sm font-bold tracking-wider uppercase font-sans">
              Help & Support
            </h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Phone className="h-4.5 w-4.5 text-teal-400 flex-shrink-0" />
                <a 
                  href={`tel:${supportPhone}`} 
                  onClick={() => onTrackSupportClick?.("call")}
                  className="hover:text-teal-300 text-gray-300 font-semibold transition-colors"
                >
                  {supportPhone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MessageSquare className="h-4.5 w-4.5 text-[#3ECFC1] flex-shrink-0" />
                <a 
                  href={`https://wa.me/${whatsappNumber}`} 
                  onClick={() => onTrackSupportClick?.("whatsapp")}
                  target="_blank" 
                  rel="noreferrer" 
                  className="hover:text-[#FF7A45] text-[#9CA3AF] font-semibold transition-colors"
                >
                  WhatsApp Support
                </a>
              </li>
            </ul>
            <div className="bg-[#1B1F24] p-3.5 rounded-lg border border-[#2A2F36]">
              <span className="block text-[10px] font-bold text-[#FF7A45] uppercase tracking-wider font-mono">
                No Advance Fees
              </span>
              <span className="block text-xs text-[#9CA3AF] mt-1">
                Beware of online scams. Pay only on delivery after satisfying yourself with product quality.
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* Local SEO Landing Hubs Section for Google Crawlers & Star Ratings */}
      <div className="border-t border-gray-900 bg-gray-950/40 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
            <h3 className="text-white text-xs font-bold tracking-widest uppercase font-mono text-center sm:text-left flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-teal-400"></span>
              <span>Popular Rental Hubs in Bhopal & Sehore (5★ Rated Support)</span>
            </h3>
            <span className="text-[10px] text-gray-500 font-mono text-center sm:text-right">
              Serving 20+ Zones • Zero Online Advance • 24h Free Delivery
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3.5">
            <a 
              href="/fridge-on-rent-in-bhopal"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Fridge on Rent in Bhopal
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                Single/Double Door • ₹700/mo
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Frost-Free & Direct Cool Units
              </span>
            </a>

            <a 
              href="/washing-machine-on-rent-in-bhopal"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Washing Machine Bhopal
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                Automatic / Semi • ₹800/mo
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Free Water Inlet Tap Setup
              </span>
            </a>

            <a 
              href="/ac-on-rent-in-bhopal"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                AC on Rent in Bhopal
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                1.5T Split / Window • Free Gas
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Same-Day Installation & Copper Pipes
              </span>
            </a>

            <a 
              href="/water-purifier-on-rent-in-bhopal"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Water Purifier Bhopal
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                RO+UV Alkaline • ₹800/mo
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Periodic Free Filter Replacements
              </span>
            </a>

            <a 
              href="/furniture-on-rent-in-bhopal"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Furniture on Rent Bhopal
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                Bed, Sofa, Wardrobe • ₹400+
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Solid Wood Diwan & Modular Units
              </span>
            </a>

            <a 
              href="/sehore/fridge-on-rent"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Fridge on Rent in Sehore
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                Zero Advance • ₹700/mo
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Chanakyapuri & Mandi Delivery
              </span>
            </a>

            <a 
              href="/sehore/washing-machine-on-rent"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Washing Machine Sehore
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                Fully Automatic • ₹800/mo
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Free Transit & Tap Accessories
              </span>
            </a>

            <a 
              href="/sehore/ac-on-rent"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                AC on Rent in Sehore
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                Split & Window Cooling
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Complete Summer Maintenance Covered
              </span>
            </a>

            <a 
              href="/sehore/water-purifier-on-rent"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Water Purifier Sehore
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                RO+UV Alkaline • ₹800/mo
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                Pure Drinking Water with Free Service
              </span>
            </a>

            <a 
              href="/sehore/cooler-on-rent"
              className="flex flex-col p-3 bg-gray-900/50 hover:bg-teal-950/20 rounded-lg border border-gray-850 hover:border-teal-500/40 transition-all text-left group"
            >
              <span className="text-xs font-bold text-gray-200 group-hover:text-teal-400 transition-colors">
                Coolers & TV in Sehore
              </span>
              <span className="text-[10px] text-gray-400 mt-1 font-mono">
                Desert Cooler & Smart TV
              </span>
              <span className="text-[9px] text-teal-400 font-sans mt-0.5">
                VIT Bhopal University Corridor Support
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Sub Footer bar */}
      <div className="bg-gray-950 border-t border-gray-900 py-6 text-center text-xs text-gray-500">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <p>2026-27 RentLow® is a registered brand under Aarabhya Rentlow services LLP</p>
          
          {/* Live Deployment Version Indicator */}
          <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-3.5 py-1.5 rounded-full text-[10.5px] font-mono text-slate-300 shadow-2xs">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="font-bold text-emerald-400">Live Build: {APP_VERSION_INFO.version}</span>
            <span className="text-slate-500">•</span>
            <span className="text-slate-400 font-medium">{APP_VERSION_INFO.buildDate}, {APP_VERSION_INFO.buildTime}</span>
          </div>

          {/* Real-time Dynamic Live Visitor Counter */}
          <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-800/80 px-3.5 py-1.5 rounded-full text-[10px] font-bold text-teal-400 font-mono tracking-wider">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
            </span>
            <span>TOTAL VISITOR COUNT: {58005 + (settings?.totalVisitors ?? 0)}</span>
          </div>

          <div className="flex items-center gap-3">
            <p className="font-mono text-[10px]">Serving comfort responsibly since 2024</p>
            <span className="text-gray-700">•</span>
            <button
              onClick={(e) => handleTabClick(e, "admin")}
              className="text-gray-500 hover:text-sky-400 transition-colors flex items-center gap-1 cursor-pointer font-mono text-[10px]"
              title="Admin Security Access"
              id="footer-admin-portal-link"
            >
              <Lock className="h-2.5 w-2.5" />
              <span>Admin Portal</span>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
